import { View, StyleSheet, Text, ScrollView, Pressable } from "react-native";
import { useUISlice } from "@/models/useUISlice";
import ModalContainer from "@/ui/ModalContainer";
import { ms } from "@/ui/moderateScale";
import { useTranslation } from "react-i18next";
import { Colors } from "@/constants/Colors";
import Input from "./Input";
import Icon from "./Icon";
import { useState, useRef, useEffect } from "react";
import { useUserSlice } from "@/models/useUserSlice";
import Menu from "./Menu";

const AlertWidgetChat = () => {
  const params = useUISlice((state) => state.alertWidgetChat);
  const setVisible = useUISlice((state) => state.setAlertWidgetChat);
  const isMobile = useUISlice((state) => state.device.isMobile);
  let chatMessages = useUserSlice((state) => {
    if (params?.note) {
      return state.notes.find((note) => note.id === params?.note?.id)?.chat
        ?.messages;
    } else if (params?.folder) {
      return state.folders.find((folder) => folder.id === params?.folder?.id)
        ?.chat?.messages;
    }
    return [];
  });
  const [message, setMessage] = useState("");
  const scrollViewRef = useRef(null);

  const width = useUISlice((state) => state.device.width);
  const height = useUISlice((state) => state.device.height);
  const { t } = useTranslation();

  // Scroll to bottom whenever messages change
  useEffect(() => {
    if (scrollViewRef.current) {
      setTimeout(() => {
        scrollViewRef.current?.scrollToEnd({ animated: true });
      }, 100);
    }
  }, [chatMessages]);

  if (!params?.note && !params?.folder) return null;
  const isNote = Boolean(params.note);
  const isDisabled = !message.trim();

  if (!chatMessages || chatMessages.length === 0) {
    chatMessages = [
      {
        role: "assistant",
        content: isNote
          ? t("alertWidgetChat.chatPlaceholderNote", {
              noteName: params.note?.name || "",
            })
          : t("alertWidgetChat.chatPlaceholderFolder", {
              folderName: params.folder?.name || "",
            }),
        createdAt: new Date().toISOString(),
      },
    ];
  }

  const _onSendMessage = () => {
    if (isDisabled) return;
    setMessage("");

    if (isNote) {
      useUserSlice.getState().note().sendMessage(params.note?.id, message);
    } else {
      useUserSlice.getState().folder().sendMessage(params.folder?.id, message);
    }

    // Scroll to bottom after sending
    setTimeout(() => {
      scrollViewRef.current?.scrollToEnd({ animated: true });
    }, 100);
  };

  const _onCopyChat = () => {
    if (isNote) {
      useUserSlice.getState().note().copyChat(params.note?.id);
    } else {
      useUserSlice.getState().folder().copyChat(params.folder?.id);
    }
  };

  const _onClearChat = () => {
    if (isNote) {
      useUserSlice.getState().note().clearChat(params.note?.id);
    } else {
      useUserSlice.getState().folder().clearChat(params.folder?.id);
    }
  };

  return (
    <ModalContainer
      shouldPersistBackdropClick
      title={
        isNote
          ? t("alertWidgetChat.titleNote")
          : t("alertWidgetChat.titleFolder")
      }
      onDismiss={() => setVisible(undefined)}
      hideCancel
      modalWidth={Math.min(width, 700)}
      statusBarBgColor="#989898"
    >
      <View style={{ height: 1, backgroundColor: Colors.gray4 }} />
      <ScrollView
        ref={scrollViewRef}
        style={StyleSheet.flatten([
          styles.chatContainer,
          { minHeight: height * 0.6, maxHeight: height * 0.6 },
        ])}
        contentContainerStyle={{ paddingBottom: ms(12) }}
      >
        {chatMessages.map((message, index) => (
          <View key={`${message.createdAt}-${index}`} style={styles.messageRow}>
            {message.role === "assistant" && (
              <View style={styles.avatar}>
                <Text>🍋</Text>
              </View>
            )}
            <View
              style={[
                styles.messageBubble,
                message.role === "user"
                  ? styles.userBubble
                  : styles.assistantBubble,
              ]}
            >
              <Text
                style={[
                  styles.messageText,
                  message.role === "user"
                    ? styles.userMessageText
                    : styles.assistantMessageText,
                ]}
              >
                {message.content}
              </Text>
            </View>
          </View>
        ))}
      </ScrollView>

      <View style={{ height: ms(24) }} />

      <View style={styles.inputContainer}>
        <Input
          placeholder={t("alertWidgetChat.inputPlaceholder")}
          onTextChange={setMessage}
          style={{
            paddingLeft: ms(48),
            borderRadius: ms(24),
            flexGrow: 1,
            paddingRight: ms(48),
          }}
          autoFocus={!isMobile}
          value={message}
          onKeyPress={(e) => {
            if (e.nativeEvent.key === "Enter" && !isDisabled) {
              _onSendMessage();
            }
          }}
        />

        <Menu
          actions={[
            {
              text: t("alertWidgetChat.menuCopy"),
              onPress: _onCopyChat,
            },
            {
              text: t("alertWidgetChat.menuClear"),
              destructive: true,
              onPress: _onClearChat,
            },
          ]}
          actionComponent={
            <View
              style={{
                ...styles.iconButton,
                backgroundColor: Colors.text + "10",
              }}
            >
              <Icon
                icon="moreHorizontal"
                style={{ ...styles.iconButtonIcon, tintColor: Colors.accent }}
              />
            </View>
          }
        />

        <Pressable
          disabled={isDisabled}
          onPress={_onSendMessage}
          style={{
            ...styles.iconButton,
            left: undefined,
            right: ms(6),
            backgroundColor: isDisabled ? Colors.gray5 : Colors.accent,
          }}
        >
          <Icon icon="send" style={styles.iconButtonIcon} />
        </Pressable>
      </View>

      <View style={styles.negativeMargin} />
    </ModalContainer>
  );
};

const styles = StyleSheet.create({
  negativeMargin: {
    marginBottom: -ms(24),
  },
  chatContainer: {
    marginHorizontal: -ms(16),
    paddingHorizontal: ms(16),
    paddingTop: ms(16),
    flex: 1,
  },
  messageRow: {
    flexDirection: "row",
    marginVertical: ms(8),
    alignItems: "flex-start",
  },
  avatar: {
    width: ms(28),
    height: ms(28),
    borderRadius: ms(14),
    backgroundColor: Colors.gray3,
    alignItems: "center",
    justifyContent: "center",
    marginRight: ms(8),
  },
  messageBubble: {
    maxWidth: "70%",
    borderRadius: ms(12),
    padding: ms(12),
    paddingVertical: ms(8),
  },
  userBubble: {
    backgroundColor: Colors.gray4,
    borderRadius: ms(12),
    alignSelf: "flex-end",
    marginLeft: "auto",
  },
  assistantBubble: {
    backgroundColor: Colors.white,
    borderWidth: 1,
    borderColor: Colors.gray2,
  },
  messageText: {
    fontSize: ms(14),
    lineHeight: ms(20),
  },
  userMessageText: {
    color: Colors.text,
  },
  assistantMessageText: {
    color: Colors.text,
  },
  inputContainer: {
    flexGrow: 1,
  },
  iconButton: {
    position: "absolute",
    bottom: ms(6),
    width: ms(36),
    height: ms(36),
    alignItems: "center",
    justifyContent: "center",
    borderRadius: ms(20),
    left: ms(6),
    backgroundColor: Colors.text,
  },
  iconButtonIcon: {
    tintColor: Colors.white,
    width: ms(20),
    height: ms(20),
  },
});

export default AlertWidgetChat;
